import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Warehouse,
  Plus,
  Minus,
  History,
  Package,
  ArrowUpDown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const InventoryUpdateDialog = ({ item, onSuccess, onClose }) => {
  const [quantity, setQuantity] = useState("");
  const [action, setAction] = useState("add");
  const [note, setNote] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!quantity || parseInt(quantity) <= 0) {
      toast.error("Bitte Menge eingeben");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/inventory/${item.product_id}`, {
        quantity: parseInt(quantity),
        action,
        note: note.trim(),
        expiry_date: expiryDate || null
      });
      
      const actionText = action === "add" ? "hinzugefügt" : action === "remove" ? "entnommen" : "gesetzt";
      toast.success(`${quantity} Stück ${actionText}`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading text-base">
          {item.product_name}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-3 pt-2">
        <div className="bg-slate-50 rounded-md p-3">
          <div className="text-xs text-slate-500">Aktueller Lagerbestand</div>
          <div className="text-2xl font-heading font-bold text-slate-900">
            {item.quantity} Stück
          </div>
          {item.expiry_date && (
            <div className="text-xs text-slate-500 mt-1">
              MHD: {new Date(item.expiry_date).toLocaleDateString("de-DE")}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-sm">Aktion</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="inventory-action-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="add">Hinzufügen</SelectItem>
              <SelectItem value="remove">Entnehmen</SelectItem>
              <SelectItem value="set">Auf Wert setzen</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label className="text-sm">Menge</Label>
          <Input
            type="number"
            min="0"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="z.B. 10"
            data-testid="inventory-quantity-input"
          />
        </div>

        {action === "add" && (
          <div className="space-y-2">
            <Label className="text-sm">MHD</Label>
            <Input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              data-testid="inventory-expiry-input"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-sm">Notiz (optional)</Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="z.B. Lieferung"
            data-testid="inventory-note-input"
          />
        </div>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            disabled={saving}
            className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C]"
            data-testid="inventory-save-btn"
          >
            {saving ? "Speichern..." : "Speichern"}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

const InventoryPage = () => {
  const [inventory, setInventory] = useState([]);
  const [logs, setLogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invRes, logRes, prodRes] = await Promise.all([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/inventory/logs?limit=50`),
        axios.get(`${API}/products`)
      ]);
      setInventory(invRes.data);
      setLogs(logRes.data);
      setProducts(prodRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  // Merge inventory with product data
  const inventoryWithProducts = inventory.map(item => {
    const product = products.find(p => p.id === item.product_id);
    return {
      ...item,
      price: product?.price || 0,
      category: product?.category || ""
    };
  }).sort((a, b) => a.product_name.localeCompare(b.product_name));

  const totalValue = inventoryWithProducts.reduce(
    (sum, item) => sum + (item.quantity * item.price), 
    0
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Lager
        </h1>
        <p className="text-slate-500 mt-1">
          Lagerbestand und Warenbewegungen
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Produkte im Lager</div>
            <div className="text-2xl font-heading font-bold">
              {inventory.length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Gesamtmenge</div>
            <div className="text-2xl font-heading font-bold text-blue-600">
              {inventory.reduce((sum, i) => sum + i.quantity, 0)} Stück
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Lagerwert</div>
            <div className="text-2xl font-heading font-bold text-emerald-600">
              {totalValue.toFixed(2)}€
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inventory Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Warehouse className="w-5 h-5" />
              Lagerbestand
            </CardTitle>
          </CardHeader>
          <CardContent>
            {inventoryWithProducts.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produkt</TableHead>
                      <TableHead>Kategorie</TableHead>
                      <TableHead className="text-right">Preis</TableHead>
                      <TableHead className="text-right">Menge</TableHead>
                      <TableHead className="text-center">MHD</TableHead>
                      <TableHead className="text-right">Wert</TableHead>
                      <TableHead className="text-right">Aktion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventoryWithProducts.map((item) => {
                      // Calculate MHD status
                      let mhdStatus = null;
                      let mhdDays = null;
                      if (item.expiry_date) {
                        const today = new Date();
                        const expiry = new Date(item.expiry_date);
                        mhdDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
                        if (mhdDays < 0) mhdStatus = "expired";
                        else if (mhdDays <= 14) mhdStatus = "critical";
                        else if (mhdDays <= 30) mhdStatus = "warning";
                      }
                      
                      return (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.product_name}
                        </TableCell>
                        <TableCell>
                          {item.category && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                              {item.category}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {item.price.toFixed(2)}€
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono font-bold ${item.quantity < 10 ? 'text-amber-600' : 'text-slate-900'}`}>
                            {item.quantity}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {item.expiry_date ? (
                            <span className={`text-xs px-2 py-1 rounded font-mono ${
                              mhdStatus === "expired" ? "bg-red-100 text-red-800" :
                              mhdStatus === "critical" ? "bg-amber-100 text-amber-800" :
                              mhdStatus === "warning" ? "bg-yellow-100 text-yellow-800" :
                              "bg-slate-100 text-slate-600"
                            }`}>
                              {new Date(item.expiry_date).toLocaleDateString("de-DE")}
                              {mhdDays !== null && mhdDays <= 30 && (
                                <span className="ml-1">
                                  ({mhdDays < 0 ? `${Math.abs(mhdDays)}T abgelaufen` : `${mhdDays}T`})
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-600">
                          {(item.quantity * item.price).toFixed(2)}€
                        </TableCell>
                        <TableCell className="text-right">
                          <Dialog open={dialogOpen && editItem?.id === item.id} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) setEditItem(null);
                          }}>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setEditItem(item);
                                  setDialogOpen(true);
                                }}
                                data-testid={`edit-inventory-${item.product_id}`}
                              >
                                <ArrowUpDown className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            {editItem?.id === item.id && (
                              <InventoryUpdateDialog 
                                item={item}
                                onSuccess={fetchData}
                                onClose={() => {
                                  setDialogOpen(false);
                                  setEditItem(null);
                                }}
                              />
                            )}
                          </Dialog>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Noch kein Lagerbestand</p>
                <p className="text-sm text-slate-400 mt-1">
                  Fügen Sie zuerst Produkte hinzu
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Activity Log */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <History className="w-5 h-5" />
              Bewegungsprotokoll
            </CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length > 0 ? (
              <div className="space-y-2">
                {logs.map((log) => (
                  <div 
                    key={log.id}
                    className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0"
                  >
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                      log.quantity_change > 0 ? 'bg-emerald-100' : 'bg-rose-100'
                    }`}>
                      {log.quantity_change > 0 ? (
                        <Plus className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <Minus className="w-5 h-5 text-rose-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{log.product_name}</div>
                      <div className="text-sm text-slate-500">
                        {log.note || (log.action === "add" ? "Hinzugefügt" : log.action === "remove" ? "Entnommen" : "Gesetzt")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono font-bold ${
                        log.quantity_change > 0 ? 'text-emerald-600' : 'text-rose-600'
                      }`}>
                        {log.quantity_change > 0 ? '+' : ''}{log.quantity_change}
                      </div>
                      <div className="text-xs text-slate-400">
                        {new Date(log.created_at).toLocaleDateString('de-DE')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Noch keine Bewegungen</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default InventoryPage;
